import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useIotsApi } from '../../../contexts/iotsApi';
import { useLanguage } from '../../../contexts/language';
import ConfirmationDialog, {
  ConsequencesList,
} from '../../../components/ConfirmationDialog';
import useApiErrorMessageGetter from '../../../hooks/useApiErrorMessage';

const CrewDeletionDialog = ({
  crewMemberId,
  onClose,
  onConfirm,
}: {
  crewMemberId: string | null;
  onClose: () => void;
  onConfirm: () => void;
}) => {
  const { deleteCrewMember } = useIotsApi();
  const { enqueueSnackbar } = useSnackbar();
  const getErrorMessage = useApiErrorMessageGetter();
  const { t } = useLanguage();

  const deleteCrewMemberMutation = useMutation({
    mutationFn: () => deleteCrewMember(crewMemberId || ''),
    onSuccess: () => {
      enqueueSnackbar({
        variant: 'success',
        message: t('content.crew-deletion-success'),
      });
      onConfirm();
    },
    onError: error => {
      enqueueSnackbar({
        variant: 'error',
        message: getErrorMessage(error, t('content.crew-deletion-error')),
      });
      deleteCrewMemberMutation.reset();
    },
  });

  return (
    <ConfirmationDialog
      open={Boolean(crewMemberId)}
      title={t('content.crew-deletion-confirmation.title')}
      onConfirm={() => {
        if (crewMemberId) {
          deleteCrewMemberMutation.mutate();
        }
      }}
      onClose={onClose}
      loading={deleteCrewMemberMutation.isPending}
    >
      <ConsequencesList
        action={t('content.crew-deletion-confirmation.action')}
        list={[
          t(
            'content.crew-deletion-confirmation.bullet-points.will-be-visually-removed',
          ),
          t(
            'content.crew-deletion-confirmation.bullet-points.submitted-journeys-unaffected',
          ),
        ]}
      />
    </ConfirmationDialog>
  );
};

export default CrewDeletionDialog;
